import React, { useState, useMemo } from 'react';
import { useStudioTheme, motion } from '../studioTheme';
import {
  StatusDot, useCursorTilt,
  healthColor, healthLabel, healthToAnimation,
  knowledgeColor, knowledgeLabel, freshness,
} from '../studioPrimitives';
import { STUDIO } from '../../../../data/agentCommandCenter';
import type { Workspace, ProductionBanner } from '../../../../data/agentCommandCenter';

// ── Helpers ───────────────────────────────────────────────────────────────────

const WORKSPACE_ACCENT: Record<string, string> = {
  frontend:    '#61dafb',
  gameplay:    '#a855f7',
  platform:    '#22c55e',
  social:      '#f97316',
  'studio-os': '#06b6d4',
};

function greeting(): string {
  const h = new Date().getHours();
  if (h < 12) return 'Good morning';
  if (h < 17) return 'Good afternoon';
  return 'Good evening';
}

const PRIORITY_RANK: Record<string, number> = { critical: 0, high: 1, medium: 2, low: 3 };
const HEALTH_RANK:   Record<string, number>  = { down: 0, degraded: 1, healthy: 2 };

function sortedQueue(workspaces: Workspace[]): Workspace[] {
  return [...workspaces].sort((a, b) => {
    const p = PRIORITY_RANK[a.state.priority] - PRIORITY_RANK[b.state.priority];
    if (p !== 0) return p;
    return HEALTH_RANK[a.state.health] - HEALTH_RANK[b.state.health];
  });
}

// ── Section label ─────────────────────────────────────────────────────────────

function SectionLabel({ left, right }: { left: string; right?: string }) {
  const { ST } = useStudioTheme();
  return (
    <div style={{ display: 'flex', alignItems: 'center', gap: ST.space[3], marginBottom: ST.space[3] }}>
      <span style={{
        fontSize:      9,
        fontWeight:    700,
        letterSpacing: '0.12em',
        textTransform: 'uppercase' as const,
        color:         ST.text.disabled,
        fontFamily:    ST.font.sans,
        flexShrink:    0,
      }}>
        {left}
      </span>
      <div style={{ flex: 1, height: 1, backgroundColor: ST.border.subtle }} />
      {right && (
        <span style={{ fontSize: 9, color: ST.text.disabled, fontFamily: ST.font.sans, flexShrink: 0 }}>
          {right}
        </span>
      )}
    </div>
  );
}

// ── Priority pill ─────────────────────────────────────────────────────────────

function PriorityPill({ priority }: { priority: string }) {
  const { ST } = useStudioTheme();
  const map: Record<string, { color: string; bg: string; border: string; label: string }> = {
    critical: { color: ST.status.down,     bg: ST.status.downBg,       border: ST.status.downBorder,     label: 'CRITICAL' },
    high:     { color: ST.status.degraded, bg: ST.status.degradedBg,   border: ST.status.degradedBorder, label: 'HIGH'     },
    medium:   { color: ST.text.accent,     bg: ST.accent.purpleSubtle, border: ST.border.accent,          label: 'MED'      },
    low:      { color: ST.text.disabled,   bg: ST.bg.overlay,           border: ST.border.subtle,         label: 'LOW'      },
  };
  const s = map[priority] ?? map.low;
  return (
    <span style={{
      display:         'inline-flex',
      alignItems:      'center',
      fontSize:        8,
      fontWeight:      700,
      fontFamily:      ST.font.sans,
      letterSpacing:   '0.10em',
      color:           s.color,
      backgroundColor: s.bg,
      border:          `1px solid ${s.border}`,
      borderRadius:    ST.radius.xs,
      padding:         '2px 6px',
      flexShrink:      0,
    }}>
      {s.label}
    </span>
  );
}

// ── Phase 2: Summary strip ────────────────────────────────────────────────────

function SummaryStrip({ workspaces }: { workspaces: Workspace[] }) {
  const { ST } = useStudioTheme();

  const healthyCount = workspaces.filter(w => w.state.health === 'healthy').length;
  const issueCount   = workspaces.filter(w => w.state.health !== 'healthy').length;
  const blockedCount = workspaces.filter(w => w.state.blockers.length > 0).length;

  const cards = [
    {
      icon:  '◈',
      label: 'Workspaces',
      value: workspaces.length,
      color: ST.text.accent,
      glow:  ST.accent.purpleGlow,
    },
    {
      icon:  '✓',
      label: 'Healthy',
      value: healthyCount,
      color: ST.status.healthy,
      glow:  ST.status.healthyBorder,
    },
    {
      icon:  '⚠',
      label: 'Issues',
      value: issueCount,
      color: issueCount > 0 ? ST.status.degraded : ST.text.disabled,
      glow:  issueCount > 0 ? ST.status.degradedBorder : 'transparent',
    },
    {
      icon:  '⊘',
      label: 'Blocked',
      value: blockedCount,
      color: blockedCount > 0 ? ST.status.down : ST.text.disabled,
      glow:  blockedCount > 0 ? ST.status.downBorder : 'transparent',
    },
  ] as const;

  return (
    <div style={{ display: 'flex', gap: ST.space[3] }}>
      {cards.map(card => (
        <div
          key={card.label}
          style={{
            flex:                 1,
            position:             'relative',
            backgroundColor:      ST.glass.surface,
            backdropFilter:       `blur(${ST.glass.blur})`,
            WebkitBackdropFilter: `blur(${ST.glass.blur})`,
            backgroundImage:      `linear-gradient(145deg, ${card.color}0c 0%, transparent 55%)`,
            border:               `1px solid ${card.color}22`,
            borderRadius:         ST.radius.lg,
            padding:              '16px 18px',
            boxShadow:            `0 0 24px ${card.glow}55, ${ST.shadow.sm}, ${ST.shadow.inset}`,
            overflow:             'hidden',
          }}
        >
          {/* Top-edge glass reflection */}
          <div style={{
            position:      'absolute', top: 0, left: 14, right: 14, height: 1,
            background:    'linear-gradient(90deg, transparent 0%, rgba(255,255,255,0.10) 30%, rgba(255,255,255,0.10) 70%, transparent 100%)',
            pointerEvents: 'none',
          }} />
          <div style={{
            display: 'flex', alignItems: 'center', gap: 6, marginBottom: 8,
          }}>
            <span style={{ fontSize: 13, color: card.color }}>{card.icon}</span>
            <span style={{
              fontSize:      9,
              fontWeight:    700,
              letterSpacing: '0.10em',
              textTransform: 'uppercase' as const,
              color:         ST.text.disabled,
              fontFamily:    ST.font.sans,
            }}>
              {card.label}
            </span>
          </div>
          <div style={{
            fontSize:      28,
            fontWeight:    800,
            color:         card.color,
            fontFamily:    ST.font.heading,
            lineHeight:    1,
            letterSpacing: '-0.04em',
            textShadow:    `0 0 24px ${card.glow}`,
          }}>
            {card.value}
          </div>
        </div>
      ))}
    </div>
  );
}

// ── Phase 2+3: Operational hero — one focal point answering "what's next?" ──────

function OperationalHero({
  workspaces,
  accentColor,
}: {
  workspaces:  Workspace[];
  accentColor: string;
}) {
  const { ST } = useStudioTheme();

  // Find the most urgent item needing attention
  const critical = workspaces.find(w => w.state.priority === 'critical');
  const blocked  = workspaces.find(w => w.state.blockers.length > 0);
  const degraded = workspaces.find(w => w.state.health !== 'healthy');
  const focal    = critical ?? blocked ?? degraded;
  const top      = sortedQueue(workspaces)[0];
  const focalWs  = focal ?? top;

  const healthyCount = workspaces.filter(w => w.state.health === 'healthy').length;
  const blockedCount = workspaces.filter(w => w.state.blockers.length > 0).length;

  // Signal palette — shifts with operational state
  const heroColor  = critical ? ST.status.down
    : (blocked || degraded) ? ST.status.degraded
    : ST.status.healthy;
  const heroBg     = critical ? ST.status.downBg
    : (blocked || degraded) ? ST.status.degradedBg
    : ST.status.healthyBg;
  const heroBorder = critical ? ST.status.downBorder
    : (blocked || degraded) ? ST.status.degradedBorder
    : ST.status.healthyBorder;
  const statusLabel = critical ? 'CRITICAL' : blocked ? 'BLOCKED' : degraded ? 'DEGRADED' : 'OPERATIONAL';
  const statusAnim  = (critical || blocked) ? 'warning' : degraded ? 'warning' : 'breathing';

  const focalPalette = ST.workspace[focalWs.id] ?? ST.workspaceFallback;

  return (
    <div style={{
      position:        'relative',
      overflow:        'hidden',
      borderRadius:    ST.radius.xl,
      padding:         '32px 40px',
      backgroundColor: ST.bg.surface,
      boxShadow:       critical
        ? `${ST.shadow.module}, 0 0 48px ${ST.status.down}18`
        : (blocked || degraded)
        ? `${ST.shadow.module}, 0 0 40px ${ST.status.degraded}14`
        : ST.shadow.module,
      transition:      'box-shadow 0.6s ease',
    }}>
      {/* Ambient blob — tinted by operational state */}
      <div
        className="studio-hero-wave-a"
        style={{
          position:      'absolute',
          top:           '-60%',
          left:          '-10%',
          width:         '55%',
          height:        '280%',
          background:    `radial-gradient(ellipse, ${heroColor}10 0%, transparent 60%)`,
          borderRadius:  '50%',
          pointerEvents: 'none',
          transition:    'background 0.8s ease',
        }}
      />
      {/* Workspace accent blob */}
      <div
        className="studio-hero-wave-b"
        style={{
          position:      'absolute',
          top:           '-30%',
          right:         '-25%',
          width:         '55%',
          height:        '230%',
          background:    `radial-gradient(ellipse, ${accentColor}0e 0%, transparent 60%)`,
          borderRadius:  '50%',
          pointerEvents: 'none',
          transition:    'background 1.4s ease',
        }}
      />
      {/* Noise texture */}
      <div style={{
        position:          'absolute',
        inset:             0,
        opacity:           0.012,
        backgroundImage:   'url("data:image/svg+xml,%3Csvg viewBox=\'0 0 256 256\' xmlns=\'http://www.w3.org/2000/svg\'%3E%3Cfilter id=\'n\'%3E%3CfeTurbulence type=\'fractalNoise\' baseFrequency=\'0.9\' numOctaves=\'4\' stitchTiles=\'stitch\'/%3E%3C/filter%3E%3Crect width=\'100%25\' height=\'100%25\' filter=\'url(%23n)\'/%3E%3C/svg%3E")',
        backgroundRepeat:  'repeat',
        backgroundSize:    '180px 180px',
        pointerEvents:     'none',
        borderRadius:      'inherit',
      }} />

      <div style={{ position: 'relative', zIndex: 1 }}>

        {/* Status badge + focal workspace identity */}
        <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 18 }}>
          <div style={{
            display:         'inline-flex',
            alignItems:      'center',
            gap:             6,
            padding:         '4px 10px',
            borderRadius:    ST.radius.sm,
            backgroundColor: heroBg,
            border:          `1px solid ${heroBorder}`,
          }}>
            <StatusDot color={heroColor} size={5} animate={statusAnim} />
            <span style={{
              fontSize:      9,
              fontWeight:    700,
              letterSpacing: '0.10em',
              color:         heroColor,
              fontFamily:    ST.font.sans,
              textShadow:    `0 0 8px ${heroColor}60`,
            }}>
              {statusLabel}
            </span>
          </div>
          <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
            <span style={{ fontSize: 14 }}>{focalWs.emoji}</span>
            <span style={{
              fontSize:   11,
              fontWeight: 600,
              color:      focalPalette.icon,
              fontFamily: ST.font.sans,
              textShadow: `0 0 10px ${focalPalette.icon}40`,
            }}>
              {focalWs.name.replace(' Workspace', '')}
            </span>
          </div>
        </div>

        {/* Operational headline — current state of focal workspace */}
        <div style={{
          fontSize:        20,
          fontWeight:      700,
          fontFamily:      ST.font.heading,
          letterSpacing:   '-0.025em',
          lineHeight:      1.3,
          color:           ST.text.primary,
          marginBottom:    14,
          overflow:        'hidden',
          display:         '-webkit-box',
          WebkitLineClamp: 2,
          WebkitBoxOrient: 'vertical',
        }}>
          {focalWs.state.currentMission}
        </div>

        {/* Next concrete action — always surfaced */}
        <div style={{
          display:         'inline-flex',
          alignItems:      'flex-start',
          gap:             6,
          fontSize:        11,
          fontWeight:      500,
          color:           ST.text.gold,
          fontFamily:      ST.font.sans,
          lineHeight:      1.55,
          padding:         '8px 14px',
          borderRadius:    ST.radius.md,
          backgroundColor: ST.accent.goldSubtle,
          border:          `1px solid ${ST.border.gold}`,
          marginBottom:    22,
          maxWidth:        '92%',
        }}>
          <span style={{ flexShrink: 0, marginTop: 1 }}>→</span>
          <span style={{
            overflow:        'hidden',
            display:         '-webkit-box',
            WebkitLineClamp: 2,
            WebkitBoxOrient: 'vertical',
          }}>
            {focalWs.state.nextAction}
          </span>
        </div>

        {/* Context strip — absorbed from summary strip */}
        <div style={{ display: 'flex', alignItems: 'center', gap: 14, flexWrap: 'wrap' as const }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 5 }}>
            <StatusDot color={ST.status.healthy} size={4} animate="breathing" />
            <span style={{ fontSize: 10, fontWeight: 600, color: ST.status.healthy, fontFamily: ST.font.sans }}>
              {healthyCount}/{workspaces.length} healthy
            </span>
          </div>
          {blockedCount > 0 && (
            <>
              <span style={{ fontSize: 10, color: ST.border.default }}>·</span>
              <div style={{ display: 'flex', alignItems: 'center', gap: 5 }}>
                <StatusDot color={ST.status.degraded} size={4} animate="warning" />
                <span style={{ fontSize: 10, fontWeight: 600, color: ST.status.degraded, fontFamily: ST.font.sans }}>
                  {blockedCount} blocked
                </span>
              </div>
            </>
          )}
        </div>
      </div>
    </div>
  );
}

// ── Phase 4: Workspace card ───────────────────────────────────────────────────

function WorkspaceCard({
  ws,
  isSelected,
  onSelect,
  index,
}: {
  ws:         Workspace;
  isSelected: boolean;
  onSelect:   (id: string) => void;
  index:      number;
}) {
  const { ST } = useStudioTheme();
  const [hovered, setHovered] = useState(false);
  const [pressed, setPressed] = useState(false);
  const { ref, tilt, onMouseMove, onMouseLeave: tiltLeave } = useCursorTilt(3);

  const palette = ST.workspace[ws.id] ?? ST.workspaceFallback;
  const hc      = healthColor(ws.state.health, ST);
  const kc      = knowledgeColor(ws.state.knowledgeStatus, ST);

  const spotlight = tilt.on
    ? `radial-gradient(circle at ${tilt.gx}% ${tilt.gy}%, ${palette.icon}12 0%, transparent 55%)`
    : 'none';

  const borderColor = isSelected
    ? `${palette.icon}55`
    : hovered
    ? `${palette.icon}30`
    : 'transparent';

  const cardShadow = isSelected
    ? `0 0 0 1px ${palette.icon}50, 0 10px 44px ${palette.icon}25, 0 2px 8px rgba(0,0,0,0.55), ${ST.shadow.inset}`
    : hovered
    ? `0 0 0 1px ${palette.icon}28, 0 8px 32px ${palette.icon}18, 0 2px 8px rgba(0,0,0,0.45), ${ST.shadow.inset}`
    : `0 2px 12px rgba(0,0,0,0.52), 0 0 0 1px rgba(255,255,255,0.06), ${ST.shadow.inset}`;

  return (
    <div
      ref={ref}
      className="studio-card-in"
      onClick={() => onSelect(ws.id)}
      onMouseEnter={() => setHovered(true)}
      onMouseLeave={() => { setHovered(false); setPressed(false); tiltLeave(); }}
      onMouseDown={() => setPressed(true)}
      onMouseUp={() => setPressed(false)}
      onMouseMove={onMouseMove}
      style={{
        position:             'relative',
        backgroundColor:      isSelected ? ST.glass.surfaceHover : ST.glass.surface,
        backdropFilter:       `blur(${ST.glass.blur})`,
        WebkitBackdropFilter: `blur(${ST.glass.blur})`,
        border:               `1px solid ${borderColor}`,
        borderRadius:         ST.radius.xxl,
        backgroundImage:      isSelected
          ? `radial-gradient(ellipse 80% 52% at 14% 0%, ${palette.icon}33 0%, transparent 62%)`
          : hovered
          ? `radial-gradient(ellipse 80% 52% at 14% 0%, ${palette.icon}26 0%, transparent 62%)`
          : `radial-gradient(ellipse 80% 52% at 14% 0%, ${palette.icon}16 0%, transparent 62%)`,
        boxShadow:            cardShadow,
        transform:            pressed
          ? 'scale(0.97) translateY(1px)'
          : tilt.on
          ? `perspective(900px) rotateX(${tilt.rx}deg) rotateY(${tilt.ry}deg) translateY(${hovered ? -3 : 0}px)`
          : `translateY(${hovered ? -5 : 0}px)`,
        transition:           tilt.on ? 'box-shadow 0.2s ease, border-color 0.2s ease' : motion(`transform ${ST.motion.spring}, box-shadow 0.22s ease, border-color 0.22s ease`),
        cursor:               'pointer',
        overflow:             'hidden',
        padding:              '14px 16px',
        animationDelay:       `${index * 60}ms`,
        display:              'flex',
        flexDirection:        'column',
        gap:                  0,
      }}
    >
      {/* Cursor spotlight */}
      {hovered && (
        <div style={{
          position:      'absolute',
          inset:         0,
          background:    spotlight,
          pointerEvents: 'none',
          borderRadius:  'inherit',
          zIndex:        0,
        }} />
      )}

      {/* Inner glass top-edge reflection */}
      <div style={{
        position:      'absolute',
        top:           0,
        left:          20,
        right:         20,
        height:        1,
        background:    'linear-gradient(90deg, transparent 0%, rgba(255,255,255,0.10) 25%, rgba(255,255,255,0.10) 75%, transparent 100%)',
        pointerEvents: 'none',
        zIndex:        2,
      }} />

      {/* Card content — above spotlight and stripe */}
      <div style={{ position: 'relative', zIndex: 2 }}>

        {/* Header row: icon + name + health */}
        <div style={{ display: 'flex', alignItems: 'flex-start', gap: 10, marginBottom: 10 }}>
          <div style={{
            width:           32,
            height:          32,
            borderRadius:    ST.radius.md,
            backgroundColor: palette.bg,
            border:          `1px solid ${palette.icon}${isSelected ? '55' : '28'}`,
            display:         'flex',
            alignItems:      'center',
            justifyContent:  'center',
            fontSize:        16,
            flexShrink:      0,
            boxShadow:       hovered || isSelected ? `0 0 22px ${palette.icon}42` : 'none',
            transition:      motion('box-shadow 0.2s ease, border-color 0.2s ease'),
          }}>
            {ws.emoji}
          </div>
          <div style={{ flex: 1, minWidth: 0 }}>
            <div style={{
              fontSize:      14,
              fontWeight:    700,
              color:         isSelected ? palette.icon : ST.text.primary,
              fontFamily:    ST.font.sans,
              lineHeight:    1.2,
              overflow:      'hidden',
              textOverflow:  'ellipsis',
              whiteSpace:    'nowrap',
              marginBottom:  3,
              letterSpacing: '-0.01em',
              textShadow:    isSelected ? `0 0 20px ${palette.icon}50` : 'none',
              transition:    motion('color 0.2s ease, text-shadow 0.2s ease'),
            }}>
              {ws.name.replace(' Workspace', '')}
            </div>
            <div style={{ display: 'flex', alignItems: 'center', gap: 5 }}>
              <StatusDot color={hc} size={5} animate={healthToAnimation(ws.state.health)} />
            </div>
          </div>
          <PriorityPill priority={ws.state.priority} />
        </div>

        {/* Mission text — 1 line by default, expands on hover/select */}
        <div style={{
          fontSize:        11,
          color:           ST.text.secondary,
          fontFamily:      ST.font.sans,
          lineHeight:      1.65,
          overflow:        'hidden',
          display:         '-webkit-box',
          WebkitLineClamp: (hovered || isSelected) ? 3 : 1,
          WebkitBoxOrient: 'vertical',
          marginBottom:    8,
          transition:      'opacity 0.2s ease',
        }}>
          {ws.state.currentMission}
        </div>

        {/* Next action or blocker — revealed on hover/select */}
        {(hovered || isSelected) && (
          <div style={{
            fontSize:        10,
            color:           ws.state.blockers.length > 0 ? ST.status.degraded : ST.text.gold,
            fontFamily:      ST.font.sans,
            lineHeight:      1.5,
            marginBottom:    8,
            overflow:        'hidden',
            display:         '-webkit-box',
            WebkitLineClamp: 1,
            WebkitBoxOrient: 'vertical',
          }}>
            {ws.state.blockers.length > 0
              ? `⚠ ${ws.state.blockers[0].replace(/🚨\s?/g, '')}`
              : `→ ${ws.state.nextAction}`
            }
          </div>
        )}

        {/* Footer: sync state + freshness */}
        <div style={{
          display:        'flex',
          alignItems:     'center',
          justifyContent: 'space-between',
        }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 4 }}>
            <StatusDot color={kc} size={4} />
          </div>
          <span style={{
            fontSize: 9, color: ST.text.disabled, fontFamily: ST.font.mono,
          }}>
            {freshness(ws.state.lastUpdated).replace('Updated ', '')}
          </span>
        </div>
      </div>

    </div>
  );
}

// ── Phase 4: Workspace card grid ──────────────────────────────────────────────

function WorkspaceCardGrid({
  workspaces,
  selectedId,
  onSelect,
}: {
  workspaces: Workspace[];
  selectedId: string | null;
  onSelect:   (id: string) => void;
}) {
  return (
    <div style={{
      display:             'grid',
      gridTemplateColumns: 'repeat(auto-fill, minmax(260px, 1fr))',
      gap:                 12,
    }}>
      {workspaces.map((ws, i) => (
        <WorkspaceCard
          key={ws.id}
          ws={ws}
          isSelected={selectedId === ws.id}
          onSelect={onSelect}
          index={i}
        />
      ))}
    </div>
  );
}

// ── Phase 5: Queue row — card style ──────────────────────────────────────────

function QueueRow({
  ws,
  isSelected,
  onSelect,
  index,
}: {
  ws:         Workspace;
  isSelected: boolean;
  onSelect:   (id: string) => void;
  index:      number;
}) {
  const { ST } = useStudioTheme();
  const [hovered, setHovered] = useState(false);
  const { ref, tilt, onMouseMove, onMouseLeave: tiltLeave } = useCursorTilt(1.5);

  const palette = ST.workspace[ws.id] ?? ST.workspaceFallback;
  const hc      = healthColor(ws.state.health, ST);
  const kc      = knowledgeColor(ws.state.knowledgeStatus, ST);

  const spotlight = tilt.on
    ? `radial-gradient(circle at ${tilt.gx}% ${tilt.gy}%, ${palette.icon}10 0%, transparent 60%)`
    : 'none';

  const handleMouseLeave = () => { setHovered(false); tiltLeave(); };

  return (
    <div
      ref={ref}
      className="studio-row-in"
      onClick={() => onSelect(ws.id)}
      onMouseEnter={() => setHovered(true)}
      onMouseLeave={handleMouseLeave}
      onMouseMove={onMouseMove}
      style={{
        position:        'relative',
        display:         'grid',
        gridTemplateColumns: '1.8fr 2.6fr 1.3fr',
        gap:             ST.space[5],
        alignItems:      'center',
        padding:         '16px 20px',
        borderRadius:    ST.radius.lg,
        backgroundColor: isSelected ? ST.accent.purpleSubtle : hovered ? ST.bg.surfaceHover : 'transparent',
        border:          `1px solid ${isSelected ? ST.border.accent : hovered ? ST.border.subtle : 'transparent'}`,
        boxShadow:       isSelected ? `0 4px 24px ${palette.icon}18, 0 0 0 1px ${ST.border.accent}` : 'none',
        cursor:          'pointer',
        transition:      tilt.on ? 'background-color 0.15s ease, border-color 0.15s ease, box-shadow 0.15s ease' : motion(ST.motion.default),
        animationDelay:  `${index * 35}ms`,
        overflow:        'hidden',
        borderLeft:      `3px solid ${isSelected ? palette.icon : 'transparent'}`,
        transform:       tilt.on
          ? `perspective(700px) rotateX(${tilt.rx}deg) rotateY(${tilt.ry}deg)`
          : 'none',
      }}
    >
      {/* Cursor spotlight */}
      {hovered && (
        <div style={{
          position:      'absolute',
          inset:         0,
          background:    spotlight,
          pointerEvents: 'none',
          borderRadius:  'inherit',
        }} />
      )}

      {/* Col 1 — Identity */}
      <div style={{ position: 'relative', display: 'flex', alignItems: 'center', gap: 10 }}>
        <div style={{
          width:           32,
          height:          32,
          borderRadius:    ST.radius.sm,
          backgroundColor: palette.bg,
          border:          `1px solid ${palette.icon}${isSelected ? '50' : '28'}`,
          display:         'flex',
          alignItems:      'center',
          justifyContent:  'center',
          fontSize:        15,
          flexShrink:      0,
          boxShadow:       hovered || isSelected ? `0 0 14px ${palette.icon}35` : 'none',
          transition:      motion('box-shadow 0.2s ease'),
        }}>
          {ws.emoji}
        </div>
        <div style={{ minWidth: 0 }}>
          <div style={{
            fontSize:     12,
            fontWeight:   600,
            color:        isSelected ? palette.icon : ST.text.primary,
            fontFamily:   ST.font.sans,
            lineHeight:   1.2,
            overflow:     'hidden',
            textOverflow: 'ellipsis',
            whiteSpace:   'nowrap',
            marginBottom: 4,
            textShadow:   isSelected ? `0 0 14px ${palette.icon}45` : 'none',
            transition:   motion('color 0.2s ease'),
          }}>
            {ws.name.replace(' Workspace', '')}
          </div>
          <PriorityPill priority={ws.state.priority} />
        </div>
      </div>

      {/* Col 2 — Next action (primary) + mission context (secondary) */}
      <div style={{ position: 'relative', minWidth: 0 }}>
        {/* Primary: what to do or what's blocking */}
        <div style={{
          fontSize:        11,
          color:           ws.state.blockers.length > 0 ? ST.status.degraded : ST.text.gold,
          fontFamily:      ST.font.sans,
          lineHeight:      1.5,
          marginBottom:    4,
          overflow:        'hidden',
          display:         '-webkit-box',
          WebkitLineClamp: 1,
          WebkitBoxOrient: 'vertical',
        }}>
          {ws.state.blockers.length > 0
            ? `⚠ ${ws.state.blockers[0].replace(/🚨\s?/g, '')}`
            : `→ ${ws.state.nextAction}`
          }
        </div>
        {/* Secondary: mission context (dimmed) */}
        <div style={{
          fontSize:        10,
          color:           ST.text.disabled,
          fontFamily:      ST.font.sans,
          lineHeight:      1.5,
          overflow:        'hidden',
          display:         '-webkit-box',
          WebkitLineClamp: (hovered || isSelected) ? 2 : 1,
          WebkitBoxOrient: 'vertical',
        }}>
          {ws.state.currentMission.replace(/🚨\s?/g, '')}
        </div>
      </div>

      {/* Col 3 — Status */}
      <div style={{ position: 'relative', display: 'flex', flexDirection: 'column', gap: 5, alignItems: 'flex-end' }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 5 }}>
          <StatusDot color={hc} size={6} animate={healthToAnimation(ws.state.health)} />
          <span style={{
            fontSize: 10, fontWeight: 600, color: hc, fontFamily: ST.font.sans,
            textShadow: `0 0 10px ${hc}65`,
          }}>
            {healthLabel(ws.state.health)}
          </span>
        </div>
        <div style={{ display: 'flex', alignItems: 'center', gap: 5 }}>
          <StatusDot color={kc} size={4} />
          <span style={{ fontSize: 9, color: ST.text.disabled, fontFamily: ST.font.sans }}>
            {knowledgeLabel(ws.state.knowledgeStatus)}
          </span>
        </div>
        <div style={{ fontSize: 9, color: ST.text.disabled, fontFamily: ST.font.mono }}>
          {freshness(ws.state.lastUpdated).replace('Updated ', '')}
        </div>
      </div>
    </div>
  );
}

// ── Phase 5: Engineering Queue ────────────────────────────────────────────────

function EngineeringQueue({
  workspaces,
  selectedId,
  onSelect,
}: {
  workspaces: Workspace[];
  selectedId: string | null;
  onSelect:   (id: string) => void;
}) {
  const { ST } = useStudioTheme();
  const queue = useMemo(() => sortedQueue(workspaces), [workspaces]);

  const inner = (
    <>
      {/* Queue header */}
      <div style={{
        display:         'flex',
        alignItems:      'center',
        justifyContent:  'space-between',
        padding:         '16px 20px',
        borderBottom:    `1px solid ${ST.border.subtle}`,
        backgroundImage: ST.gradient.hero,
      }}>
        <div style={{
          fontSize:      12,
          fontWeight:    600,
          fontFamily:    ST.font.sans,
          color:         ST.text.secondary,
          letterSpacing: '-0.01em',
        }}>
          Engineering Queue
        </div>
      </div>

      {/* Rows */}
      <div style={{ padding: '6px 4px', display: 'flex', flexDirection: 'column', gap: 2 }}>
        {queue.map((ws, i) => (
          <QueueRow
            key={ws.id}
            ws={ws}
            isSelected={selectedId === ws.id}
            onSelect={onSelect}
            index={i}
          />
        ))}
      </div>
    </>
  );

  return (
    <div style={{
      borderRadius:         ST.radius.xl,
      overflow:             'hidden',
      boxShadow:            `${ST.shadow.card}, ${ST.shadow.inset}`,
      backgroundColor:      ST.glass.surface,
      backdropFilter:       `blur(${ST.glass.blur})`,
      WebkitBackdropFilter: `blur(${ST.glass.blur})`,
      border:               `1px solid ${ST.border.subtle}`,
    }}>
      {inner}
    </div>
  );
}

// ── Production bar ────────────────────────────────────────────────────────────

function ProductionBar({
  production,
  workspaces,
}: {
  production: ProductionBanner;
  workspaces: Workspace[];
}) {
  const { ST } = useStudioTheme();
  const [expanded, setExpanded] = useState(false);
  const healthy     = production.health === 'healthy';
  const statusColor = healthy ? ST.status.healthy : ST.status.degraded;

  return (
    <div style={{
      backgroundColor: ST.bg.surface,
      border:          `1px solid ${ST.border.subtle}`,
      borderRadius:    ST.radius.lg,
      overflow:        'hidden',
      boxShadow:       ST.shadow.sm,
    }}>
      <div
        onClick={() => setExpanded(s => !s)}
        style={{
          display:        'flex',
          alignItems:     'center',
          justifyContent: 'space-between',
          padding:        '12px 16px',
          cursor:         'pointer',
        }}
      >
        <div style={{ display: 'flex', alignItems: 'center', gap: ST.space[3] }}>
          <StatusDot color={statusColor} size={7} animate={healthy ? 'breathing' : 'warning'} />
          <span style={{ fontSize: 11, fontWeight: 600, color: ST.text.secondary, fontFamily: ST.font.sans }}>
            Production
          </span>
          <span style={{
            fontSize: 11, fontWeight: 600, color: statusColor, fontFamily: ST.font.sans,
            textShadow: `0 0 12px ${statusColor}60`,
          }}>
            {production.health.toUpperCase()}
          </span>
        </div>
        <div style={{ display: 'flex', alignItems: 'center', gap: ST.space[4] }}>
          <span style={{ fontSize: 10, color: ST.text.disabled, fontFamily: ST.font.mono }}>
            {production.branch} · {production.commit}
          </span>
          <span style={{ fontSize: 10, color: ST.text.disabled, fontFamily: ST.font.sans }}>
            {freshness(production.lastDeploy).replace('Updated ', 'Deployed ')}
          </span>
          <span style={{
            fontSize: 10, color: ST.text.disabled,
            transition: motion(ST.motion.fast),
            transform:  expanded ? 'rotate(180deg)' : 'rotate(0deg)',
            display:    'inline-block',
          }}>
            ∨
          </span>
        </div>
      </div>

      {expanded && (
        <div style={{ borderTop: `1px solid ${ST.border.subtle}`, padding: '12px 16px 16px' }}>
          <div style={{
            display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)',
            gap: `${ST.space[2]}px ${ST.space[4]}px`, marginBottom: ST.space[3],
          }}>
            {[
              { label: 'Branch',    value: production.branch,                                        mono: true  },
              { label: 'Commit',    value: production.commit,                                        mono: true, color: ST.text.gold },
              { label: 'Deploy',    value: freshness(production.lastDeploy).replace('Updated ', ''), mono: false },
              { label: 'Typecheck', value: '✓ Passing',                                              color: ST.status.healthy },
              { label: 'Build',     value: '✓ Passing',                                              color: ST.status.healthy },
              { label: 'Health',    value: production.health.toUpperCase(),                          color: statusColor },
            ].map(({ label, value, mono, color }) => (
              <div key={label}>
                <div style={{
                  fontSize: 8, letterSpacing: '0.10em', textTransform: 'uppercase',
                  color: ST.text.disabled, fontFamily: ST.font.sans, fontWeight: 600, marginBottom: 2,
                }}>
                  {label}
                </div>
                <div style={{
                  fontSize: 11, fontWeight: 600,
                  color: color ?? ST.text.secondary,
                  fontFamily: mono ? ST.font.mono : ST.font.sans,
                }}>
                  {value}
                </div>
              </div>
            ))}
          </div>
          <div style={{
            fontSize: 8, letterSpacing: '0.10em', textTransform: 'uppercase',
            color: ST.text.disabled, fontFamily: ST.font.sans, fontWeight: 600, marginBottom: ST.space[2],
          }}>
            Workspace Knowledge
          </div>
          <div style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
            {workspaces.map(ws => {
              const kc = knowledgeColor(ws.state.knowledgeStatus, ST);
              return (
                <div key={ws.id} style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                    <span style={{ fontSize: 11 }}>{ws.emoji}</span>
                    <span style={{ fontSize: 10, color: ST.text.muted, fontFamily: ST.font.sans }}>
                      {ws.name.replace(' Workspace', '')}
                    </span>
                  </div>
                  <div style={{ display: 'flex', alignItems: 'center', gap: 5 }}>
                    <StatusDot color={kc} size={5} />
                    <span style={{ fontSize: 10, fontWeight: 600, color: kc, fontFamily: ST.font.sans }}>
                      {knowledgeLabel(ws.state.knowledgeStatus)}
                    </span>
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      )}
    </div>
  );
}

// ── Mission banner ────────────────────────────────────────────────────────────

function MissionBanner({ mission, workspaces }: { mission: string; workspaces: Workspace[] }) {
  const { ST } = useStudioTheme();
  const healthyCount = workspaces.filter(w => w.state.health === 'healthy').length;

  return (
    <div style={{
      display:         'flex',
      alignItems:      'center',
      gap:             ST.space[4],
      padding:         '16px 20px',
      borderRadius:    ST.radius.lg,
      backgroundImage: ST.gradient.gold,
      border:          `1px solid ${ST.border.gold}`,
      boxShadow:       ST.shadow.goldGlow,
    }}>
      <span style={{ fontSize: 20, flexShrink: 0 }}>🎯</span>
      <div style={{ flex: 1, minWidth: 0 }}>
        <div style={{
          fontSize: 9, color: ST.text.gold, fontFamily: ST.font.sans, fontWeight: 600,
          letterSpacing: '0.08em', textTransform: 'uppercase', marginBottom: 3,
        }}>
          Studio Mission
        </div>
        <div style={{
          fontSize: 13, fontWeight: 500, color: ST.text.primary, fontFamily: ST.font.heading,
          lineHeight: 1.4, fontStyle: 'italic', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap',
        }}>
          "{mission}"
        </div>
      </div>
      <div style={{
        fontSize: 11, fontWeight: 600, color: ST.text.gold, fontFamily: ST.font.sans, flexShrink: 0,
      }}>
        {healthyCount}/{workspaces.length} healthy
      </div>
    </div>
  );
}

// ── Empty search ──────────────────────────────────────────────────────────────

function EmptySearch({ query }: { query: string }) {
  const { ST } = useStudioTheme();
  return (
    <div style={{ padding: `${ST.space[10]}px ${ST.space[4]}px`, textAlign: 'center' }}>
      <div style={{ fontSize: 28, marginBottom: ST.space[3] }}>🔍</div>
      <div style={{
        fontSize: 14, fontWeight: 600, color: ST.text.secondary,
        fontFamily: ST.font.heading, marginBottom: ST.space[1],
      }}>
        No results for "{query}"
      </div>
      <div style={{ fontSize: 11, color: ST.text.disabled, fontFamily: ST.font.sans }}>
        Try a workspace name or mission keyword.
      </div>
    </div>
  );
}

// ── Main export ───────────────────────────────────────────────────────────────

export function WorkspaceGrid({
  workspaces,
  production,
  mission,
  selectedId,
  onSelect,
  searchQuery,
  onSearchChange,
}: {
  workspaces:     Workspace[];
  production:     ProductionBanner;
  mission:        string;
  selectedId:     string | null;
  onSelect:       (id: string) => void;
  searchQuery:    string;
  onSearchChange: (q: string) => void;
}) {
  const { ST } = useStudioTheme();
  const accentColor = selectedId ? (WORKSPACE_ACCENT[selectedId] ?? '#7c3aed') : '#7c3aed';

  return (
    <div>
      {/* ── Minimal sticky search bar ────────────────────────────── */}
      <div style={{
        display:              'flex',
        alignItems:           'center',
        justifyContent:       'flex-end',
        padding:              '10px 24px',
        borderBottom:         `1px solid ${ST.border.subtle}`,
        position:             'sticky',
        top:                  0,
        zIndex:               10,
        backgroundColor:      ST.bg.base,
        backdropFilter:       `blur(${ST.glass.blur})`,
        WebkitBackdropFilter: `blur(${ST.glass.blur})`,
      }}>
        <div style={{
          display:              'flex',
          alignItems:           'center',
          gap:                  8,
          padding:              '0 12px',
          borderRadius:         ST.radius.lg,
          backgroundColor:      ST.glass.surface,
          backdropFilter:       `blur(${ST.glass.blur})`,
          WebkitBackdropFilter: `blur(${ST.glass.blur})`,
          border:               `1px solid ${searchQuery ? ST.border.default : ST.glass.border}`,
          width:                200,
          height:               32,
          transition:           motion(ST.motion.default),
        }}>
          <span style={{ fontSize: 11, color: ST.text.disabled, flexShrink: 0 }}>⌕</span>
          <input
            type="text"
            value={searchQuery}
            onChange={e => onSearchChange(e.target.value)}
            placeholder="Search workspaces..."
            style={{
              flex:            1,
              border:          'none',
              outline:         'none',
              backgroundColor: 'transparent',
              fontSize:        12,
              color:           ST.text.secondary,
              fontFamily:      ST.font.sans,
              minWidth:        0,
            }}
          />
          {searchQuery && (
            <button
              onClick={() => onSearchChange('')}
              style={{
                border: 'none', backgroundColor: 'transparent', color: ST.text.disabled,
                cursor: 'pointer', fontSize: 14, lineHeight: 1, padding: 0, flexShrink: 0,
              }}
            >
              ×
            </button>
          )}
        </div>
      </div>

      {/* ── Content ──────────────────────────────────────────────── */}
      <div style={{ padding: '20px 24px 48px' }}>
        {workspaces.length === 0 ? (
          <EmptySearch query={searchQuery} />
        ) : (
          <div style={{ display: 'flex', flexDirection: 'column', gap: 20 }}>

            {/* Operational hero — one focal point */}
            <OperationalHero workspaces={STUDIO.workspaces} accentColor={accentColor} />

            {/* Phase 4 — Workspace cards */}
            <div>
              <SectionLabel left="Workspaces" right={`${STUDIO.workspaces.length} total`} />
              <WorkspaceCardGrid
                workspaces={workspaces}
                selectedId={selectedId}
                onSelect={onSelect}
              />
            </div>

            {/* Phase 5 — Engineering queue */}
            <div>
              <SectionLabel left="Engineering Queue" right="sorted by priority" />
              <EngineeringQueue
                workspaces={workspaces}
                selectedId={selectedId}
                onSelect={onSelect}
              />
            </div>

            {/* Production + mission */}
            <ProductionBar production={production} workspaces={STUDIO.workspaces} />
            <MissionBanner mission={mission} workspaces={STUDIO.workspaces} />

          </div>
        )}
      </div>
    </div>
  );
}
